<template>
    <PageLoading v-if="isLoading" />

    <div v-else class="main-wrap home-page">
        <div class="slider-wrap">
            <transition name="fade" mode="out-in">
                <div v-if="isFetchingSlider" class="slider-spinner">
                    <q-spinner-pie color="primary" size="40px" />
                </div>

                <q-carousel
                    v-else
                    v-model="activeSlide"
                    transition-prev="slide-right"
                    transition-next="slide-left"
                    :autoplay="autoplay"
                    animated
                    arrows
                    navigation
                    infinite
                    swipeable
                    height="100%"
                    @mouseenter="autoplay = false"
                    @mouseleave="autoplay = true"
                >
                    <q-carousel-slide
                        v-for="(slide, index) in slider"
                        :key="index"
                        class="slide-wrap"
                        :img-src="slide.image"
                        :name="index"
                    >
                        <div class="slide-content">
                            <div class="slide-overlay">
                                <h1>
                                    {{
                                        JSONParseTranslations(
                                            slide.title,
                                            locale.key
                                        )
                                    }}
                                </h1>

                                <div class="tagline">
                                    {{
                                        JSONParseTranslations(
                                            slide.subtitle,
                                            locale.key
                                        )
                                    }}
                                </div>

                                <q-btn
                                    class="slide-btn"
                                    color="black"
                                    text-color="white"
                                    :to="slide.button_url"
                                >
                                    <span class="text-primary q-ml-xs">
                                        {{
                                            JSONParseTranslations(
                                                slide.button_text,
                                                locale.key
                                            )
                                        }}
                                    </span>
                                </q-btn>
                            </div>
                        </div>
                    </q-carousel-slide>
                </q-carousel>
            </transition>
        </div>

        <!-- Featured courses -->
        <template v-if="featuredCourses.featured_courses.length">
            <h2>
                {{
                    JSONParseTranslations(
                        featuredCourses.featured_courses_title.title,
                        locale.key
                    )
                }}
            </h2>

            <div class="courses-wrap featured-courses">
                <CourseThumb
                    v-for="(
                        course, courseIndex
                    ) in featuredCourses.featured_courses"
                    :key="courseIndex"
                    :index="courseIndex"
                    :course="course.courses"
                />
            </div>
        </template>

        <!-- Latest news posts -->
        <template v-if="posts.length >= 1">
            <div class="bg-wrap bg-black q-py-md">
                <h2 class="text-primary">{{ $t('general.last_news') }}</h2>

                <div class="news-wrap featured-news">
                    <template v-for="(news, index) in posts">
                        <NewsThumb
                            v-if="index <= 2"
                            :key="index"
                            :index="index"
                            :news="news"
                        />
                    </template>
                </div>

                <div
                    class="row justify-center q-mb-xl"
                    style="margin-top: -40px"
                >
                    <q-btn
                        class="q-mx-lg btn-custom"
                        color="white"
                        size="lg"
                        flat
                        dense
                        @click="$router.push({ name: 'News' })"
                    >
                        {{ $t('general.view_more') }} +
                    </q-btn>
                </div>
            </div>
        </template>

        <!-- Upcoming classes -->
        <template v-if="upcomingClasses.length >= 1">
            <h2>{{ $t('courses.upcoming_classes') }}</h2>

            <div class="courses-wrap featured-courses">
                <ClassThumb
                    v-for="(classItem, index) in upcomingClasses"
                    :key="index"
                    :index="index"
                    :class-item="classItem"
                    :course="classItem.courses"
                />
            </div>

            <div class="row justify-center q-mb-xl" style="margin-top: -40px">
                <q-btn
                    class="q-mx-lg btn-custom"
                    color="secondary"
                    size="lg"
                    flat
                    dense
                    @click="$router.push({ name: 'Classes' })"
                >
                    {{ $t('general.view_courses_calendar') }}
                </q-btn>
            </div>
        </template>

        <!-- Featured partners -->
        <template v-if="featuredPartners.featured_partners.length">
            <div class="bg-wrap bg-primary q-py-md">
                <h2>
                    {{
                        JSONParseTranslations(
                            featuredPartners.featured_partners_title.title,
                            locale.key
                        )
                    }}
                </h2>

                <div class="partners">
                    <router-link
                        v-for="(
                            partner, partnerIndex
                        ) in featuredPartners.featured_partners"
                        :key="partnerIndex"
                        class="partner-logo"
                        :to="{
                            name: 'PartnersDetails',
                            params: { partnerId: partner.id },
                        }"
                    >
                        <img v-if="partner.logo" :src="partner.logo" alt="" />
                    </router-link>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import translate from '@/local-shared/mixins/translate'
import PageLoading from '@/components/PageLoading'
import CourseThumb from '@/components/CourseThumb'
import NewsThumb from '@/components/NewsThumb'
import ClassThumb from '@/components/ClassThumb'
import homepageService from '@/services/modules/homepage'
// import coursesService from '@/services/modules/courses'
import postsService from '@/services/modules/posts'
import classesService from '@/services/modules/classes'
import picturesService from '@/services/pictures'
import encoders from '@/helpers/encoders'
import moment from 'moment'
import store from '@/store'
import _ from 'lodash'
import { slugs } from '@/local-shared/mixins/slugs'

const loadingKeys = {
    fetchingSlider: 'fetching-slider',
    fetchingCourses: 'fetching-courses',
    fetchingPartners: 'fetching-partners',
    fetchingClasses: 'fetching-classes',
    fetchingPosts: 'fetching-posts',
}

const publishedPostStatusId = 2

export default {
    name: 'Home',

    components: {
        PageLoading,
        CourseThumb,
        ClassThumb,
        NewsThumb,
    },

    mixins: [translate],

    data() {
        return {
            activeSlide: 0,

            slider: [],

            featuredPartners: {
                featured_partners: [],
                featured_partners_title: {},
            },

            featuredCourses: {
                featured_courses: [],
                featured_courses_title: {},
            },

            upcomingClasses: [],

            posts: [],

            autoplay: true,

            slugs: slugs,
        }
    },

    computed: {
        locale() {
            return store.getters['locale/currentLocale']
        },

        isLoading() {
            return (
                this.isFetchingCourses ||
                this.isFetchingPartners ||
                this.isFetchingClasses
            )
        },

        isFetchingPosts() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingPosts
            ).length
        },

        isFetchingSlider() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingSlider
            ).length
        },

        isFetchingCourses() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingCourses
            ).length
        },

        isFetchingPartners() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingPartners
            ).length
        },

        isFetchingClasses() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingClasses
            ).length
        },
    },

    async created() {
        await this.fetchFeaturedCourses()
        await this.fetchFeaturedPartners()
        await this.fetchUpcomingClasses()
        await this.fetchPosts()
    },

    mounted() {
        this.fetchSlider()
    },

    methods: {
        async fetchSlider() {
            this.loadingAdd(loadingKeys.fetchingSlider)

            let parameters = {
                filter: {
                    enabled: true,
                },
                orderBy: [
                    {
                        column: 'rank',
                        direction: 'asc',
                    },
                ],
            }

            let response = await homepageService.banners.retrieve(parameters)

            if (response.status === 200) {
                let banners = response.data.results

                await Promise.all(
                    banners.map(async (banner) => {
                        let imageResponse = await picturesService.retrieveById(
                            banner.picture_id
                        )
                        if (imageResponse.status === 200) {
                            banner.image = encoders.arrayBufferToBase64(
                                imageResponse
                            )
                        } else {
                            banner.image = require('@/assets/images/banner/slide_img_01_min.jpg')
                        }
                    })
                )

                this.slider = _.cloneDeep(banners)
            }

            this.loadingRemove(loadingKeys.fetchingSlider)
        },

        async fetchFeaturedCourses() {
            this.loadingAdd(loadingKeys.fetchingCourses)

            let parameters = {
                orderBy: [
                    {
                        column: 'rank',
                        direction: 'asc',
                    },
                ],
                relations: {
                    courses: true,
                },
            }

            let response = await homepageService.featuredCourses.retrieve(
                parameters
            )

            if (response.status === 200) {
                this.featuredCourses = response.data.results[0]
            }

            this.loadingRemove(loadingKeys.fetchingCourses)
        },

        async fetchFeaturedPartners() {
            this.loadingAdd(loadingKeys.fetchingPartners)

            let parameters = {
                orderBy: [
                    {
                        column: 'rank',
                        direction: 'asc',
                    },

                    {
                        column: 'created_at',
                        direction: 'desc',
                    },
                ],
            }

            let response = await homepageService.featuredPartners.retrieve(
                parameters
            )

            if (response.status === 200) {
                let partners = response.data.results[0]

                await Promise.all(
                    partners.featured_partners.map(async (partner) => {
                        let imageResponse = await picturesService.retrieveById(
                            partner.partners_featuredPartners
                                .darklogo_picture_id
                        )

                        if (imageResponse.status === 200) {
                            partner.logo = encoders.arrayBufferToBase64(
                                imageResponse
                            )
                        } else {
                            partner.logo = require('@/assets/logo.svg')
                        }
                    })
                )

                this.featuredPartners = _.cloneDeep(partners)
            }

            this.loadingRemove(loadingKeys.fetchingPartners)
        },

        async fetchUpcomingClasses() {
            this.loadingAdd(loadingKeys.fetchingClasses)

            let parameters = {
                filter: {
                    enabled: true,
                    date: {
                        begin:
                            moment(new Date()).format('YYYY-MM-DD') +
                            'T00:00:00',
                        end: null,
                    },
                    statusSlugIn: [
                        this.slugs.classesStatus.opened,
                        this.slugs.classesStatus.closed,
                        this.slugs.classesStatus.concluded,
                    ],
                },

                orderBy: [
                    {
                        column: 'start_date',
                        direction: 'asc',
                    },
                ],

                pagination: {
                    perPage: 3,
                },

                relations: {
                    courses: true,
                    countActiveEnrollments: true,
                },
            }

            let response = await classesService.retrieve(parameters)

            if (response.status === 200) {
                this.upcomingClasses = response.data.results
            } else {
                this.notifyError(response)
            }

            this.loadingRemove(loadingKeys.fetchingClasses)
        },

        async fetchPosts() {
            this.loadingAdd(loadingKeys.fetchingPosts)

            let parameters = {
                orderBy: [
                    {
                        column: 'created_at',
                        direction: 'desc',
                    },
                ],

                filter: {
                    enabled: true,
                    status: publishedPostStatusId,
                    isBetweenVisibleDates: true,
                },
            }

            let response = await postsService.retrieve(parameters)

            if (response.status === 200) {
                this.posts = response.data.results
            } else {
                this.notifyError(response)
            }

            this.loadingRemove(loadingKeys.fetchingPosts)
        },
    },
}
</script>
